import { OpenAPI, V1Service } from '@zelos/codegen/apis';
import { accessCookieParserRetrieveForServerSide } from '@zelos/shared/lib/access-cookie';
import axios from 'axios';
import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from 'next';
import { getServerSideApiBasement } from './get-server-side-api-basement';

type SessionApiCaller = <T>(sessionApiCaller: () => Promise<T>) => Promise<T>;

type CreatePrivateRouteGetServerSidePropsProps<P extends Record<string, unknown>> = {
  alert?: 'need_authentication',
  callback?: (apiCaller: SessionApiCaller, ...props: Parameters<GetServerSideProps<P>>) => ReturnType<GetServerSideProps<P>>,
  destination?: string,
};

type CreateSigendInCheckGetServerSideProps<P extends Record<string, unknown>> = {
  callback: (isSignedIn: boolean, apiCaller: SessionApiCaller, ...props: Parameters<GetServerSideProps<P>>) => ReturnType<GetServerSideProps<P>>,
};

const getCookieValue = (cookie: string, name: string) => cookie.match(new RegExp(`${name}=([^;]+)`))?.[1];

const createRedirect = (alert: string, destination: string) => {
  const searchParams = new URLSearchParams({
    alert,
    destination,
  });
  return (): GetServerSidePropsResult<never> => ({
    redirect: {
      statusCode: 302,
      destination: `/sign?${searchParams}`,
    },
  });
};

const refershSession = (context: GetServerSidePropsContext) => {
  return new Promise<ReturnType<typeof accessCookieParserRetrieveForServerSide>>((resolve, reject) => {
    const {
      req,
      res,
    } = context;

    const responseInterceptor = axios.interceptors.response.use((response) => {
      axios.interceptors.response.eject(responseInterceptor);
      const setCookie = response.headers['set-cookie'];
      let newAccessCookie: string | null = null;
      if (setCookie) {
        res.setHeader('set-cookie', setCookie);
        for (const cookie of setCookie) {
          const access = getCookieValue(cookie, 'access');
          if (access) {
            newAccessCookie = access;
            break;
          }
        }
      }
      if (newAccessCookie) {
        resolve(accessCookieParserRetrieveForServerSide(newAccessCookie));
      } else {
        reject(new Error('Failed to refresh session'));
      }
      return response;
    }, (error) => {
      axios.interceptors.response.eject(responseInterceptor);
      return Promise.reject(error);
    });

    req.headers.cookie && (
      OpenAPI.HEADERS = {
        Cookie: req.headers.cookie,
      });

    V1Service.v1UsersTokenRefreshCreate().catch((err) => {
      reject(err);
    }).finally(() => {
      OpenAPI.HEADERS = undefined;
    });
  });
};

export const serverSidePropsSessionApiCaller = (context: GetServerSidePropsContext): SessionApiCaller => {
  let accessCookie = accessCookieParserRetrieveForServerSide(context.req.cookies?.access);

  return async (sessionApiCaller) => {
    try {
      OpenAPI.BASE = getServerSideApiBasement();
      accessCookie.valueOf() && accessCookie.isExpired() && (
        accessCookie = await refershSession(context)
      );
      OpenAPI.TOKEN = accessCookie.valueOf() || undefined;
      return await sessionApiCaller();
    } finally {
      OpenAPI.TOKEN = undefined;
    }
  };
};

export const createPrivateRouteGetServerSideProps = <P extends Record<string, unknown> = {}>({
  alert = 'need_authentication',
  callback = () => Promise.resolve({ props: {} as P }),
  destination,
}: CreatePrivateRouteGetServerSidePropsProps<P> = {}): GetServerSideProps => async (context) => {
  const { req, res, resolvedUrl } = context;
  const redirect = createRedirect(alert, destination || resolvedUrl);

  const accessCookie = accessCookieParserRetrieveForServerSide(req.cookies.access);

  if (!accessCookie.valueOf() && !accessCookie.isNeedRefresh(res.getHeader('set-cookie')))
    return redirect();

  return await callback(serverSidePropsSessionApiCaller(context), context);
};

export const createSigendInCheckGetServerSideProps = <P extends Record<string, unknown> = {}>({
  callback,
}: CreateSigendInCheckGetServerSideProps<P>): GetServerSideProps => (context) => {
  const { req } = context;
  const accessCookie = accessCookieParserRetrieveForServerSide(req.cookies.access || '');

  return callback(!accessCookie.isExpired(), serverSidePropsSessionApiCaller(context), context);
};
